<template>
  <div ref="root" class="c-proxy-tab">
    <p class="c-proxy-tab__description">
      Your organization can connect to Salesforce through a proxy. Crossbeam
      then signs in to Salesforce and syncs your data through the proxy URL.
      Setting, changing or removing it means signing in to Salesforce again.
    </p>
    <BittsInput
      v-model="fields.proxyUrl"
      name="salesforce-proxy-url"
      data-testid="salesforce-proxy-url"
      form-label="Proxy URL"
      placeholder="https://sf-proxy.acme.com"
      :status="fieldStatus"
      :danger-text="fieldError"
      :disabled="!canManageDataSources || feedIsReauthorizing"
      @blur="handleProxyUrlBlur"
    />
    <div class="c-proxy-tab__actions">
      <BittsButton
        v-if="hasStoredProxyUrl(feed)"
        text="Remove and reauthorize"
        type="danger"
        variant="outline"
        :disabled="!canManageDataSources || feedIsReauthorizing"
        data-testid="c-proxy-tab-remove"
        @click="handleRemove"
      />
      <BittsButton
        text="Save and reauthorize"
        :disabled="isSaveDisabled"
        data-testid="c-proxy-tab-save"
        @click="handleSave"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { BittsButton, BittsInput } from '@crossbeam/bitts';

import { computed, useTemplateRef } from 'vue';

import useAuth from '@/composables/useAuth';
import useDataSourceSettings from '@/composables/useDataSourceSettings';
import useFeedDetails from '@/composables/useFeedDetails';
import useSalesforceProxyConnection from '@/composables/useSalesforceProxyConnection';
import useSalesforceProxyGate from '@/composables/useSalesforceProxyGate';
import { captureException } from '@/errors';
import { useFlashesStore } from '@/stores';
import { focusFirstInvalidField } from '@/utils/focusFirstInvalidField';

type Props = {
  feedId: number;
};
const { feedId } = defineProps<Props>();

const flashesStore = useFlashesStore();
const { feed } = useDataSourceSettings();
const { reauthorize, feedIsReauthorizing } = useFeedDetails({ feedId });
const { hasStoredProxyUrl } = useSalesforceProxyGate();

const { hasPermission } = useAuth();
const canManageDataSources = computed(() =>
  hasPermission('manage:data-sources'),
);

const {
  fields,
  isUnchanged,
  fieldErrors,
  touch,
  proxyUrlFormProperties,
  clearedProxyFormProperties,
  absorbAuthorizeRejection,
} = useSalesforceProxyConnection(() => feed.value?.custom_base_url);

const root = useTemplateRef('root');

/* Only the URL has a field here, so a rejection about the credentials shows
   under it too. */
const fieldError = computed(
  () => Object.values(fieldErrors.value).find(Boolean) ?? '',
);
const fieldStatus = computed<'danger' | undefined>(() =>
  fieldError.value ? 'danger' : undefined,
);

const isSaveDisabled = computed(
  () =>
    !canManageDataSources.value ||
    feedIsReauthorizing.value ||
    !fields.proxyUrl.trim() ||
    isUnchanged.value,
);

function handleProxyUrlBlur() {
  if (fields.proxyUrl.trim()) touch('proxyUrl');
}

async function handleSave() {
  const formProperties = proxyUrlFormProperties();
  if (!formProperties) {
    touch('proxyUrl');
    await focusFirstInvalidField(root);
    return;
  }
  await reauthorizeWith(formProperties);
}

async function handleRemove() {
  await reauthorizeWith(clearedProxyFormProperties);
}

async function reauthorizeWith(
  formProperties: NonNullable<ReturnType<typeof proxyUrlFormProperties>>,
) {
  if (!feed.value) return;
  try {
    await reauthorize(feed.value, formProperties);
  } catch (err) {
    if (absorbAuthorizeRejection(err)) return;
    captureException(err);
    flashesStore.addErrorFlash({
      message: 'Could not reauthorize feed',
      description: 'If the error persists contact support@crossbeam.com',
    });
  }
}
</script>

<style lang="pcss" scoped>
@reference "../../../../../css/theme.css";
.c-proxy-tab {
  @apply flex flex-col gap-16;
}

.c-proxy-tab__description {
  @apply text-neutral-text;
}

.c-proxy-tab__actions {
  @apply flex justify-end gap-8;
}
</style>
